import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api from '../utils/api';

const AuthContext = createContext(null);

const normalizeUser = (user) => ({
  ...user,
  firstName: user.firstName || user.first_name,
  lastName: user.lastName || user.last_name,
  phoneNumber: user.phoneNumber || user.phone_number,
  emailVerified: user.emailVerified ?? user.email_verified,
  isFirstLogin: user.isFirstLogin ?? user.first_time_login,
  termsAccepted: user.termsAccepted ?? user.terms_accepted,
  assignedBranches: user.assignedBranches || user.assigned_branches || [],
  businessId: user.businessId || user.business_id,
  isSuperAdmin: user.isSuperAdmin ?? (user.role === 'super_admin'),
  // add more fields as needed
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await api.get('/profile/me');

      setUser(normalizeUser(response.data.profile));
      setError(null);
    } catch (error) {
      // Clear local storage and axios headers
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);

      // Handle specific error types
      if (error.response?.data?.error === 'TOKEN_EXPIRED') {
        setError('Your session has expired. Please log in again.');
      } else if (error.response?.status === 401) {
        setError(null); // Don't show error for invalid tokens, just redirect
      } else {
        setError('Failed to verify authentication. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Use user data from login response or fetch profile if needed
      if (userData) {
        setUser(normalizeUser(userData));
      } else {
        // Fetch full profile after login
        const profileRes = await api.get('/profile/me');
        setUser(normalizeUser(profileRes.data.profile));
      }
      setError(null);
    } catch (error) {
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 